/**
 * PaieHub — Batch #14.250
 * Hub du sidebar > Paie. Sélecteur de dossier (expertise `paie`) + tuiles :
 * Aperçu · Employés · Périodes de paie · Bulletins · Remises DAS · CCQ · Réglages.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  HandCoins, Users, ChevronsUpDown, Check, Loader2, LayoutDashboard, CalendarClock, FileText, Landmark, HardHat, Settings2, Globe, Lock, FileBadge, FileOutput,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { API, authH } from "./paie/_shared";
import { TONE_BG } from "@/constants/tones";

export const PAIE_TILES = [
  { id: "apercu", title: "Aperçu", desc: "Masse salariale · Alertes", icon: LayoutDashboard, tone: "orange" },
  { id: "employes", title: "Employés", desc: "Fiches · Taux · Retenues", icon: Users, tone: "sky" },
  { id: "periodes", title: "Périodes de paie", desc: "Heures → Calcul → Paiement", icon: CalendarClock, tone: "emerald" },
  { id: "bulletins", title: "Bulletins", desc: "PDF · Chèques · Envoi", icon: FileText, tone: "indigo" },
  { id: "remises", title: "Remises DAS", desc: "Fédéral · Québec · DAS client", icon: Landmark, tone: "rose" },
  { id: "ccq", title: "CCQ", desc: "Métiers · Rapport mensuel", icon: HardHat, tone: "amber" },
  { id: "fin-annee", title: "Fin d'année", desc: "T4 · Relevés 1 · Sommaires", icon: FileBadge, tone: "violet" },
  { id: "releves-emploi", title: "Relevés d'emploi", desc: "RE au départ · RE Web", icon: FileOutput, tone: "rose" },
  { id: "reglages", title: "Réglages", desc: "Employeur · Banque · Tables", icon: Settings2, tone: "slate" },
];


/** Batch #14.251 — interrupteur tuile portail « Paie » (même matrice que Gestion des accès). */
function PortalControls({ master }) {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!master?.id) return;
    setEnabled(null);
    axios.get(`${API}/admin/payroll/${master.id}/settings`, { headers: authH() })
      .then((r) => setEnabled(!!r.data.portal_tile_enabled))
      .catch(() => setEnabled(false));
  }, [master?.id]);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !enabled;
      const r = await axios.get(`${API}/masters/${master.id}/portal-permissions`, { headers: authH() });
      const members = ["primary", ...(r.data?.has_secondary ? ["secondary"] : [])];
      await Promise.all(members.map((member) => axios.put(`${API}/masters/${master.id}/portal-permissions`,
        { member, permissions: { paie: next } }, { headers: authH() })));
      setEnabled(next);
      toast.success(next ? "Tuile « Paie » visible dans le portail du client" : "Tuile « Paie » retirée du portail");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="paie-portal-controls">
      <button type="button" onClick={toggle} disabled={busy || enabled === null}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${enabled ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
        data-testid="paie-portal-tile-toggle">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
        Tuile portail : {enabled === null ? "…" : enabled ? "activée" : "désactivée"}
      </button>
      <Link to={`/admin/portail-clients?tab=acces&master_id=${master.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-xs text-slate-600 hover:bg-slate-50" data-testid="paie-portal-access-link">
        <Lock className="w-3.5 h-3.5" /> Gestion des accès
      </Link>
    </div>
  );
}

export default function PaieHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [masters, setMasters] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get("master_id") || "");
  const [openCombo, setOpenCombo] = useState(false);
  const [pendingRuns, setPendingRuns] = useState(null);
  const [pending, setPending] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/payroll/pending-summary`, { headers: authH() })
      .then((r) => setPending(r.data?.by_master || {}))
      .catch(() => setPending({}));
  }, [selectedId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/masters?expertise=paie`, { headers: authH() });
        setMasters(r.data?.items || []);
        if (!selectedId && (r.data?.items || []).length === 1) setSelectedId(r.data.items[0].id);
      } catch {
        toast.error("Erreur chargement des dossiers");
      } finally {
        setLoadingList(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) setSearchParams({ master_id: selectedId }, { replace: true });
  }, [selectedId, setSearchParams]);

  useEffect(() => {
    if (!selectedId) return;
    setPendingRuns(null);
    axios.get(`${API}/admin/payroll/${selectedId}/overview`, { headers: authH() })
      .then((r) => setPendingRuns({ pending: r.data?.pending_runs?.length || 0, due: r.data?.remittances_due?.length || 0, active: r.data?.employees?.active || 0 }))
      .catch(() => setPendingRuns(null));
  }, [selectedId]);

  const selectedMaster = useMemo(() => masters.find((m) => m.id === selectedId), [masters, selectedId]);

  const openTile = useCallback((tile) => {
    if (!selectedId) { toast.message("Sélectionnez un dossier client"); return; }
    navigate(`/admin/paie/${selectedId}/${tile.id}`);
  }, [navigate, selectedId]);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="paie-hub">
      <div className="bg-gradient-to-r from-orange-50 via-orange-50 to-white border-2 border-orange-200 rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-orange-600 text-white flex items-center justify-center shadow">
            <HandCoins className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Paie</h1>
            <p className="text-xs text-slate-600 mt-0.5">
              Employés, feuilles d'heures, bulletins, chèques, dépôt direct et remises DAS (ARC / Revenu Québec). Sélectionnez un dossier client.
            </p>
          </div>
          {selectedMaster && (
            <div className="flex flex-col items-end gap-2">
              <PortalControls master={selectedMaster} />
              {pendingRuns && (
                <div className="flex items-center gap-2 text-[11px] flex-wrap justify-end" data-testid="paie-hub-summary">
                  <span className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-600">{pendingRuns.active} employé(s) actif(s)</span>
                  {pendingRuns.pending > 0 && <span className="px-2 py-1 rounded-lg bg-sky-100 border border-sky-200 text-sky-800 font-semibold">{pendingRuns.pending} période(s) à traiter</span>}
                  {pendingRuns.due > 0 && <span className="px-2 py-1 rounded-lg bg-rose-100 border border-rose-200 text-rose-800 font-semibold">{pendingRuns.due} remise(s) à faire</span>}
                  {pending[selectedMaster.id] > 0 && <span className="px-2 py-1 rounded-lg bg-amber-100 border border-amber-200 text-amber-800 font-semibold" data-testid="paie-hub-client-pending">{pending[selectedMaster.id]} saisie(s) client à vérifier</span>}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-5">
          <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">Dossier client (employeur)</label>
          <Popover open={openCombo} onOpenChange={setOpenCombo}>
            <PopoverTrigger asChild>
              <button type="button" aria-expanded={openCombo} disabled={loadingList}
                className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm text-left transition shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="paie-hub-combobox-trigger">
                <span className="truncate flex-1">
                  {loadingList ? (
                    <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</span>
                  ) : selectedMaster ? (
                    <span className="text-slate-800 font-medium">{selectedMaster.couple_display_name || "(sans nom)"}</span>
                  ) : (
                    <span className="text-slate-400">Choisir un dossier client…</span>
                  )}
                </span>
                <ChevronsUpDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un dossier…" />
                <CommandList>
                  <CommandEmpty>
                    {masters.length === 0
                      ? "Aucun dossier avec l'expertise Paie activée — activez-la dans la fiche du dossier client (Cabinet › Master)."
                      : "Aucun résultat."}
                  </CommandEmpty>
                  <CommandGroup heading={`${masters.length} dossier(s)`}>
                    {masters.map((m) => (
                      <CommandItem key={m.id}
                        value={`${m.couple_display_name || ""} ${m.primary_name || ""} ${m.secondary_name || ""} ${m.primary_email || ""}`}
                        onSelect={() => { setSelectedId(m.id); setOpenCombo(false); }}
                        className="flex items-start gap-2 py-2 cursor-pointer" data-testid={`paie-hub-master-${m.id}`}>
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedId === m.id ? "opacity-100 text-orange-600" : "opacity-0"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate flex items-center gap-2">
                            <span className="truncate">{m.couple_display_name || "(sans nom)"}</span>
                            {pending[m.id] > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid={`paie-hub-pending-${m.id}`}>{pending[m.id]}</span>
                            )}
                          </div>
                          {(m.primary_name || m.secondary_name) && (
                            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                              <Users className="w-3 h-3" />{m.primary_name || "?"}{m.secondary_name ? ` + ${m.secondary_name}` : ""}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {!selectedId ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center">
          <HandCoins className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-700">Sélectionnez un dossier client</h3>
          <p className="text-xs text-slate-500 mt-1">Les modules de paie apparaîtront ci-dessous.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {PAIE_TILES.map((t) => (
            <button key={t.id} onClick={() => openTile(t)}
              className={`group bg-gradient-to-br ${TONE_BG[t.tone]} border-2 rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg`}
              data-testid={`paie-hub-tile-${t.id}`}>
              <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
                <t.icon className="w-7 h-7" />
              </div>
              <span className="font-semibold text-sm mb-0.5">{t.title}</span>
              <span className="text-[10px] opacity-75">{t.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
